import { saveAs } from "file-saver";
import { normalizeBookingAdditionalEquipment } from "./BookingAdditionalEquipment";
import { getBookingReportChecklist, matchesBookingReportFilter } from "./BookingReportChecklistUtils";

export const BOOKING_EXPORT_CHUNK_SIZE = 250;
const DAY_MS = 86400000;

// Keep date-only fields intact; timestamps use the booking table's Bangkok date.
export const bookingExcelDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let year, month, day;
  const dateOnly = typeof value === "string" && value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    [, year, month, day] = dateOnly.map(Number);
  } else {
    let date;
    if (value && typeof value.toDate === "function") date = value.toDate();
    else if (value && typeof value.seconds === "number") date = new Date(value.seconds * 1000);
    else if (value instanceof Date || typeof value === "string") date = new Date(value);
    if (!date || Number.isNaN(date.getTime())) return null;
    const bangkok = new Date(date.getTime() + 7 * 3600000);
    year = bangkok.getUTCFullYear();
    month = bangkok.getUTCMonth() + 1;
    day = bangkok.getUTCDate();
  }
  const midnight = Date.UTC(year, month - 1, day);
  const date = new Date(midnight);
  if (year < 1900 || year > 9999 || date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return (midnight - Date.UTC(1899, 11, 30)) / DAY_MS;
};

export const loadBookingExportBookings = async ({
  fetchChunk,
  filterDocs,
  loadReports,
  reportBy = "all",
  isCurrent = () => true,
  onProgress = () => {},
  chunkSize = BOOKING_EXPORT_CHUNK_SIZE,
}) => {
  const bookings = [];
  const reportsByHn = new Map();
  let cursor;
  let scanned = 0;
  const assertCurrent = () => {
    if (!isCurrent()) throw new Error("Export stopped because the page, filters, or signed-in account changed. Please export again.");
  };
  while (true) {
    assertCurrent();
    const docs = await fetchChunk(cursor);
    assertCurrent();
    const accessibleDocs = filterDocs(docs);
    // Bound report reads, and share them when multiple bookings have the same HN.
    for (let offset = 0; offset < accessibleDocs.length; offset += 10) {
      assertCurrent();
      const batch = await Promise.all(accessibleDocs.slice(offset, offset + 10).map(async (doc) => {
        const booking = doc.data();
        if (reportBy === "all") return booking;
        const props = booking.extendedProps || {};
        const hn = String(props.hn || "").trim();
        if (hn && !reportsByHn.has(hn)) reportsByHn.set(hn, loadReports(hn));
        const reports = hn ? await reportsByHn.get(hn) : [];
        return matchesBookingReportFilter(getBookingReportChecklist(reports, props), reportBy)
          ? booking : null;
      }));
      assertCurrent();
      bookings.push(...batch.filter(Boolean));
    }
    scanned += docs.length;
    onProgress({ scanned, matched: bookings.length });
    // Advance through raw query results, even if access/report filters reject a whole page.
    if (docs.length < chunkSize) break;
    cursor = docs[docs.length - 1];
  }
  return bookings;
};

export const buildBookingExportWorkbook = async (XlsxPopulate, bookings) => {
  const headers = [
    "Node", "Customer", "Sleep Study Order", "Name", "HN", "Type",
    "Additional Equipment", "Status", "Sleep Study Date", "Current Date",
    "Recording Tech", "Scoring Tech", "Doctor", "Sale",
  ];
  const text = (value) => value === null || value === undefined ? "" : String(value);
  const rows = bookings.map((booking) => {
    const props = booking.extendedProps || {};
    return [
      text(props.node), text(props.customer), text(props.order),
      [props.firstname, props.lastname].filter(Boolean).join(" "), text(props.hn), text(props.type),
      normalizeBookingAdditionalEquipment(props.additionalEquipment).join(", "), text(props.status),
      bookingExcelDate(props.latestSleepStudyDate), bookingExcelDate(booking.start),
      text(props.techrecord), text(props.technician), text(props.doctor), text(props.sale),
    ];
  });
  const workbook = await XlsxPopulate.fromBlankAsync();
  const sheet = workbook.sheet(0).name("Bookings");
  sheet.cell("A1").value([headers, ...rows]);
  sheet.usedRange().style({ fontFamily: "Tahoma", fontSize: 10, verticalAlignment: "top", wrapText: true });
  sheet.range(1, 1, 1, headers.length).style({ fill: "17365D", fontColor: "FFFFFF", bold: true });
  sheet.row(1).height(48);
  [24, 30, 22, 32, 18, 24, 24, 26, 20, 20, 26, 26, 26, 26].forEach((width, index) => sheet.column(index + 1).width(width));
  if (rows.length) sheet.range(2, 9, rows.length + 1, 10).style("numberFormat", "yyyy-mm-dd");
  // Match the reference workbook's column menus and keep them visible while scrolling.
  sheet.autoFilter(sheet.range(1, 1, rows.length + 1, headers.length));
  sheet.freezePanes(2, 1);
  return workbook;
};

export const downloadBookingExport = async (bookings, isCurrent = () => true) => {
  // Use the unminified browser bundle, as in the other Excel exports. The legacy
  // build toolchain corrupts the vendor's embedded blank workbook if minified twice.
  const module = await import("xlsx-populate/browser/xlsx-populate-no-encryption.js");
  const workbook = await buildBookingExportWorkbook(module.default || module, bookings);
  const blob = await workbook.outputAsync({ type: "blob" });
  if (!isCurrent()) throw new Error("Export stopped because the page, filters, or signed-in account changed. Please export again.");
  const stamp = new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 19).replace(/[T:]/g, "-");
  saveAs(blob, `bookings-${stamp}.xlsx`);
};
